import React from 'react';
import clsx from 'clsx';
import {useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useMatches, Await, useFetcher} from '@remix-run/react';

//domains
import {MARSELLO_PORTAL_URL} from '~/lib/const';
import {colourToClassName} from 'domains/sanity';

//components
import SanityImage from '../SanityImage';

import SanityRichText from 'components/modules/SanityRichText';

//data
import type {EmailSignUp} from 'domains/root';

type FormValues = {
  email: string;
};

const schema = yup
  .object({
    email: yup
      .string()
      .email('Please enter a valid email')
      .required('Please enter your email'),
  })
  .required();

export default function EmailSignUp(props: any) {
  const [root] = useMatches();
  const emailSignUp = root?.data?.emailSignUp as EmailSignUp;
  const bgColour = colourToClassName(emailSignUp.bg_colour?.title, 'bg');
  const textColour = colourToClassName(emailSignUp.text_colour?.title, 'text');
  const buttonBgColour = colourToClassName(
    emailSignUp.button_bg_colour?.title,
    'bg',
  );
  const buttonTextColour = colourToClassName(
    emailSignUp.button_text_colour?.title,
    'text',
  );

  const {
    formState: {errors, isDirty, isSubmitting, isSubmitSuccessful, isSubmitted},
    register,
  } = useForm<
    FormValues & {
      serverError?: string;
    }
  >({
    defaultValues: {
      email: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const formRef = React.useRef<HTMLFormElement>(null);
  const fetcher = useFetcher();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formRef?.current) {
      const formData = new FormData(formRef.current);
      fetcher.submit(formData, {method: 'post', action: '/api/subscribe'});
    }
  };

  const response = fetcher?.data && JSON.parse(fetcher?.data);

  return (
    <section
      id="email-signup"
      className={clsx([
        'module--email-signup min-h-[350px] relative',
        'bg-white',
        textColour,
      ])}
    >
      <React.Suspense fallback="loading">
        <Await resolve={emailSignUp}>
          {(emailSignUp) => {
            return (
              <div
                className={clsx(
                  'grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12',
                  bgColour,
                )}
              >
                {/* 迎新優惠 as real, crawlable text — this used to be a
                    baked-in coupon image (bad for SEO). Styled after that
                    image's hang-tag design: white tags with a punched hole,
                    steel-blue offer text, navy validity badge. */}
                <div className="p-4 pb-0 lg:p-12 lg:pr-0 xl:p-16 xl:pr-0">
                  <div className="grid grid-cols-2 gap-4 md:gap-6">
                    {[
                      {
                        label: '迎新優惠一',
                        title: '免費送您一升偈油',
                        desc: '成為會員並馬上預約!',
                      },
                      {
                        label: '迎新優惠二',
                        title: '購滿一千 即減一百',
                        desc: '成為會員馬上享用優惠',
                      },
                    ].map((offer) => (
                      <div key={offer.label} className="drop-shadow-lg">
                        <div className="bg-white px-3 pb-5 pt-3 text-center [clip-path:polygon(0_14%,15%_0,85%_0,100%_14%,100%_100%,0_100%)] md:px-5 md:pb-7">
                          <span className="mx-auto mb-3 mt-3 block h-5 w-5 rounded-full bg-hk-deep-blue/80 md:mb-4 md:h-7 md:w-7" />
                          <p className="mb-1 text-sm font-bold tracking-widest text-hk-deep-blue md:text-md">
                            {offer.label}
                          </p>
                          <h4 className="mb-2 text-lg font-bold leading-snug text-[#54759C] md:mb-3 md:text-[26px]">
                            {offer.title}
                          </h4>
                          <p className="mb-3 text-xs font-medium text-hk-deep-blue md:mb-4 md:text-sm">
                            {offer.desc}
                          </p>
                          <p className="mx-auto inline-block bg-[#54759C] px-2 py-1 text-[10px] text-white md:text-xs">
                            兌換限期為此電郵發出日起三十天
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center p-6 pt-2 lg:p-12 lg:pl-0 xl:p-16 xl:pl-0">
                  <div className="w-full max-w-xl">
                    <p className="mb-3 flex items-center gap-3 text-sm font-medium uppercase tracking-widest text-hk-orange">
                      <span className="inline-block h-[2px] w-8 bg-hk-orange"></span>
                      {emailSignUp.section_title}
                    </p>
                    <h3 className="mb-6 text-xl font-bold leading-snug lg:text-[28px] lg:leading-[40px]">
                      {emailSignUp.button_text}
                    </h3>
                    <ul className="mb-8 space-y-3 text-md font-normal opacity-90">
                      {[
                        '透過消費賺取積分',
                        '兌換免費禮品及現金券',
                        '獲取第一手新品資訊及優惠',
                      ].map((perk) => (
                        <li key={perk} className="flex items-center gap-3">
                          <svg
                            className="h-5 w-5 shrink-0"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="10"
                              cy="10"
                              r="10"
                              className="fill-hk-orange"
                            />
                            <path
                              d="M6 10.5L8.8 13L14 7.5"
                              stroke="white"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {perk}
                        </li>
                      ))}
                    </ul>
                    {/* Membership signup happens in the Marsello customer
                        portal now (site-account registration was retired). */}
                    <a
                      href={MARSELLO_PORTAL_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={clsx([
                        buttonTextColour,
                        buttonBgColour,
                        'inline-flex items-center gap-2 rounded-full px-10 py-4 text-md font-semibold shadow-lg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:opacity-90 lg:text-[18px]',
                      ])}
                    >
                      立即加入會員
                      <span aria-hidden>↗</span>
                    </a>
                  </div>
                </div>
                {/* <fetcher.Form
                  method="post"
                  action="/api/subscribe"
                  onSubmit={handleSubmit}
                  ref={formRef}
                >
                  <div className="flex items-center p-4 lg:p-12 xl:p-20">
                    <div className="w-full">
                      {response && response.errorMessage && (
                        <div className="mb-6 flex items-center justify-center rounded-sm border border-red p-4 text-sm text-red">
                          <p>{response.errorMessage}</p>
                        </div>
                      )}
                      {response && response.success && (
                        <div className="mb-6 text-center leading-snug rounded-sm bg-hk-orange p-4 text-sm text-white">
                          <SanityRichText
                            richtext={emailSignUp.thank_you_message}
                          />
                        </div>
                      )}
                      <h3 className="mb-9 text-md font-medium lg:text-[20px] lg:leading-[30px]">
                        {emailSignUp.section_title}
                      </h3>
                      <label
                        htmlFor="email"
                        className="mb-4 block text-md lg:text-[20px] font-semibold"
                      >
                        {emailSignUp.label}
                      </label>
                      <input
                        type="text"
                        {...register('email')}
                        placeholder="Enter Your Email"
                        disabled={response && response?.success}
                        className="mb-4 block w-full rounded-md border-2 border-current bg-transparent p-5 text-md placeholder:text-opacity-30 placeholder:opacity-30"
                      />
                      {errors.email?.message && (
                        <div className="mb-4 text-sm text-red">
                          {errors.email?.message}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={
                          !isDirty ||
                          isSubmitting ||
                          isSubmitSuccessful ||
                          (response && response.success)
                        }
                        className={clsx([
                          buttonTextColour,
                          buttonBgColour,
                          'w-full rounded-md py-5 text-md lg:text-[20px] transition-opacity duration-200 ease-out hover:opacity-75 disabled:opacity-50',
                        ])}
                      >
                        {fetcher.state === 'submitting'
                          ? 'Subitting...'
                          : response && response?.success
                          ? 'Subscribed'
                          : emailSignUp.button_text}
                      </button>
                    </div>
                  </div>
                </fetcher.Form> */}
              </div>
            );
          }}
        </Await>
      </React.Suspense>
    </section>
  );
}
